import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { LeadForm } from "@/components/forms/LeadForm";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { useSiteContent } from "@/hooks/useSiteContent";

const categories = [
  { label: "All Projects", value: "all" },
  { label: "Solar Projects", value: "solar" },
  { label: "Roofing Projects", value: "roofing" },
  { label: "Energy Solutions", value: "energy" },
];

export default function GalleryPage() {
  const { siteData } = useSiteContent();
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredProjects = useMemo(() => {
    if (activeCategory === "all") {
      return siteData.projects;
    }

    return siteData.projects.filter((project) => project.category === activeCategory);
  }, [activeCategory, siteData.projects]);

  return (
    <div className="space-y-24 pb-6" data-testid="gallery-page">
      <PageHero description="A project gallery helps visitors see the range of your work, from rooftop upgrades to solar-ready energy improvements." eyebrow="Project gallery" imageUrl={siteData.hero.background_image} phone={siteData.phone} primaryHref="#gallery-quote-form" primaryLabel="Start your project" testIdPrefix="gallery-hero" title="Browse recent residential and commercial work across Southern California." />

      <section className="px-4 md:px-6" data-testid="gallery-grid-section">
        <div className="page-section space-y-10">
          <SectionHeading description="Filter between project categories to keep the gallery organized and easy to scan for homeowners and commercial prospects." eyebrow="Before and after inspiration" testIdPrefix="gallery-grid-heading" title="Visual proof of the projects Monarch helps bring to life." />
          <div className="flex flex-wrap gap-3" data-testid="gallery-filter-list">
            {categories.map((category) => (
              <button
                className={`rounded-full px-5 py-3 text-sm font-semibold transition-colors ${
                  activeCategory === category.value ? "bg-primary text-primary-foreground" : "border border-primary/15 bg-white/80 text-foreground"
                }`}
                data-testid={`gallery-filter-${category.value}-button`}
                key={category.value}
                onClick={() => setActiveCategory(category.value)}
                type="button"
              >
                {category.label}
              </button>
            ))}
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredProjects.map((project) => (
              <Card className="overflow-hidden rounded-[2rem] border-white/60 bg-white/90" data-testid={`gallery-project-card-${project.id}`} key={project.id}>
                <img alt={project.title} className="aspect-[4/3] w-full object-cover" data-testid={`gallery-project-image-${project.id}`} src={project.image_url} />
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary" data-testid={`gallery-project-category-${project.id}`}>
                      {project.category}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid={`gallery-project-location-${project.id}`}>
                      {project.location}
                    </p>
                  </div>
                  <h3 className="text-2xl font-bold text-foreground" data-testid={`gallery-project-title-${project.id}`}>
                    {project.title}
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid={`gallery-project-summary-${project.id}`}>
                    {project.summary}
                  </p>
                  <div className="rounded-[1.2rem] bg-primary/5 px-4 py-4" data-testid={`gallery-project-result-${project.id}`}>
                    <p className="text-sm text-foreground">{project.result}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="gallery-quote-section" id="gallery-quote-form">
        <div className="page-section">
          <LeadForm buttonLabel="Start your project" description="Ready to add your property to the next success story? Request a quote here." leadType="quote" pageSource="gallery-quote" testIdPrefix="gallery-quote" title="Talk with Monarch about your next project" />
        </div>
      </section>
    </div>
  );
}